import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  actionAlert,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  InputLabel,
  SplitTextField,
  TextField,
  Typography,
} from "@suraasa/placebo-ui"
import { useQueryClient } from "@tanstack/react-query"
import api from "api"
import { queries } from "api/queries"
import { CartlessOrder } from "api/resources/growthspace/types"
import { OrderItemDetails, ProductDetails } from "api/resources/orders/types"
import { PhoneNumber } from "api/resources/users/types"
import { AISparkleFilled } from "assets/icons/system/AISparkle"
import BookClosed from "assets/icons/system/BookClosed"
import RazorpayLogo from "assets/razorpayLogo.webp"
import StripeLogo from "assets/stripeLogo.webp"
import { AuthManager } from "features/Authentication/AuthManager"
import LogoBg from "features/Payment/EmbeddedPayment/assets/logo-bg.webp"
import FeatureCard from "features/Payment/EmbeddedPayment/FeatureCard"
import { context } from "global/Context/context"
import DefaultLogo from "growthspace/DefaultLogo"
import { GraphUp, NavArrowDown, Reports } from "iconoir-react"
import { useEffect, useState } from "react"
import { useContext } from "react"
import { Controller, useForm } from "react-hook-form"
import { useNavigate, useSearchParams } from "react-router-dom"
import routes from "routes"
import countryCodes from "utils/countryCodes"
import { buildUserName, getPlatformURL, mapErrorsV2 } from "utils/helpers"
import useCurrentCountry from "utils/hooks/useCurrentCountry"
import useFormatPrice from "utils/hooks/useFormatPrice"
import useHideGlobalBottomNavigation from "utils/hooks/useGlobalHideBottomNavigation"
import useHideTopNavigation from "utils/hooks/useHideTopNavigation"
import { useResource } from "utils/hooks/useResource"

import { usePayment } from "../hooks/usePayment"
import AssessmentIcon from "./assets/AssessmentIcon"

type BillingForm = {
  fullName: string
  email: string
  phoneNumber: PhoneNumber
  billingFullName?: string
  billingEmail?: string
}

const options = countryCodes.map(({ dialCode }) => ({
  value: dialCode,
  label: dialCode,
}))

const notifyParent = (
  type: "PAYMENT_SUCCESS" | "PAYMENT_FAILURE" | "PAYMENT_READY",
  parentOrigin: string,
  error?: string
) => {
  window.parent.postMessage(
    { source: "suraasa-payment", type, ...(error && { error }) },
    parentOrigin
  )
}

const EmbeddedPayment = () => {
  useHideGlobalBottomNavigation()
  useHideTopNavigation()
  const [searchParams] = useSearchParams()
  const parentOrigin = searchParams.get("origin")
  const qrCodeId = searchParams.get("qrCodeId")
  const [order, setOrder] = useState<CartlessOrder | null>(null)
  const [orderDetails, setOrderDetails] = useState<OrderItemDetails | null>(
    null
  )
  const [productDetails, setProductDetails] = useState<ProductDetails | null>(
    null
  )
  const [checkingOut, setCheckingOut] = useState(false)
  const [stripeForm, setStripeForm] = useState<JSX.Element | null>(null)
  const [hasDifferentBilling, setHasDifferentBilling] = useState(false)
  const formatPrice = useFormatPrice()
  const { currencies } = useResource(["currencies"])
  const country = useCurrentCountry()
  const isIndia = country?.isoCode === "IN"

  const { currentGrowthspace } = useContext(context)
  const authInfo = AuthManager.getPlatformAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const paymentRequired = currentGrowthspace?.paymentDetails?.paymentRequired

  const handlePaymentSuccess = async () => {
    if (parentOrigin) {
      notifyParent("PAYMENT_SUCCESS", parentOrigin)
      return
    }

    await queryClient.refetchQueries({
      queryKey: queries.growthspace.list().queryKey,
    })

    // Standalone (non-iframe) case only — this is where the app's
    // GrowthspacePaymentPendingPage sends a user in a real browser tab, with
    // no parent window to notify. Without this, a successful payment here
    // jumped straight to routes.home with no confirmation at all. Same
    // pattern already used for CPAT payment success
    // (CartlessPaymentFlow/useCPATPayment.tsx).
    const goHome = () => navigate(routes.home)

    await actionAlert.success({
      title: "Payment Successful!",
      duration: 5000,
      onClose: goHome,
    })

    // actionAlert.success's onClose option is silently dropped by the
    // library (ActionAlertProvider merges `{...options, onClose: internalFn}`,
    // so the internal handler always wins) — the awaited promise resolving
    // once the alert closes is the only signal that actually fires. Same
    // workaround as useCPATPayment.tsx's onSuccess.
    goHome()
  }

  const getCurrencySymbol = (code: string) =>
    currencies.find(c => c.code === code)?.symbol ?? code

  useEffect(() => {
    if (!paymentRequired) return
    if (searchParams.get("order_status") === "success") {
      handlePaymentSuccess()
      return
    }
    const init = async () => {
      try {
        const res = await api.growthspace.createTransaction({
          params: {
            ...(qrCodeId && { qr_code_id: encodeURIComponent(qrCodeId) }),
          },
        })
        setOrder(res)
        const details = await api.orders.getOrderDetails({
          urlParams: { id: String(res.polluxOrderId) },
        })
        setOrderDetails(details)

        if (res.id != null) {
          try {
            const productDetails = await api.orders.getProductDetails({
              urlParams: { id: res.id.toString() },
            })
            setProductDetails(productDetails)
          } catch (error) {
            console.error("Error while fetching product details: ", error)
            setProductDetails(null)
          }
        }

        if (parentOrigin) {
          notifyParent("PAYMENT_READY", parentOrigin)
        }
      } catch (error) {
        mapErrorsV2(error)
      }
    }
    init()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentRequired])

  const {
    register,
    handleSubmit,
    control,
    setError,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<BillingForm>({
    defaultValues: {
      email: authInfo?.user.email,
      fullName: authInfo?.user ? buildUserName(authInfo?.user) : "",
    },
  })

  useEffect(() => {
    if (!country?.isoCode) return
    const dialCode = countryCodes.find(
      c => c.code === country.isoCode
    )?.dialCode
    if (!dialCode) return

    setValue("phoneNumber", { code: dialCode, number: "" })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country?.isoCode])

  const purchaseDetails = order
    ? {
        itemName: order.licenseNames.join(", "),
        amount: Number(order.amount),
        currency: {
          code: order.currency.code,
          symbol: getCurrencySymbol(order.currency.code),
        },
      }
    : null

  const embeddedRedirectURL = window.location.href

  const { checkout } = usePayment({
    stripeConfig: {
      paymentKey: import.meta.env.VITE_STRIPE_PAYMENT_KEY,
      logo: () => (
        <img
          src="https://assets.suraasa.com/logos/v2/mini.webp"
          alt="logo"
          className="me-1.5 size-10"
        />
      ),
      getForm: setStripeForm,
      handleClose: () => {
        setStripeForm(null)
      },
      redirectURL: embeddedRedirectURL,
    },
    razorpayConfig: {
      logo: "https://assets.suraasa.com/logos/v2/mini.webp",
      successHandler: handlePaymentSuccess,
    },
    successURL: embeddedRedirectURL,
    purchaseDetails,
  })

  const onSubmit = handleSubmit(async formData => {
    try {
      const billingFullName =
        hasDifferentBilling && formData.billingFullName
          ? encodeURIComponent(formData.billingFullName)
          : null
      const billingEmail =
        hasDifferentBilling && formData.billingEmail
          ? encodeURIComponent(formData.billingEmail)
          : null
      const billingPhoneNumber =
        formData.phoneNumber?.code && formData.phoneNumber?.number
          ? encodeURIComponent(
              `${formData.phoneNumber.code}${formData.phoneNumber.number}`
            )
          : null

      const params = {
        ...(qrCodeId && { qr_code_id: encodeURIComponent(qrCodeId) }),
        ...(billingFullName
          ? { "billing_details.full_name": billingFullName }
          : {}),
        ...(billingEmail ? { "billing_details.email": billingEmail } : {}),
        ...(billingPhoneNumber
          ? { "billing_details.phone_number": billingPhoneNumber }
          : {}),
      }

      const res = await api.growthspace.createTransaction({
        params,
      })
      setOrder(res)

      // Immediately trigger checkout
      setCheckingOut(true)
      try {
        const details = await api.orders.getOrderDetails({
          urlParams: { id: String(res.polluxOrderId) },
        })
        setOrderDetails(details)
        const paymentPlanId = details.paymentPlans[0]?.id
        if (!paymentPlanId) throw new Error("Payment plan not found")
        await checkout(paymentPlanId)
      } catch {
        if (parentOrigin) {
          notifyParent("PAYMENT_FAILURE", parentOrigin, "Checkout failed")
        }
      } finally {
        setCheckingOut(false)
      }
    } catch (error) {
      mapErrorsV2(error, {
        setter: setError,
        fields: [
          ["billingDetails.phoneNumber", "phoneNumber"],
          ["billingDetails.fullName", "billingFullName"],
          ["billingDetails.email", "billingEmail"],
        ],
      })
    }
  })

  if (checkingOut) {
    return (
      <div className="mx-auto flex min-h-[400px] w-full max-w-2xl flex-col items-center justify-center">
        <CircularProgress size="lg" />
        <Typography className="mt-3 text-onSurface-500">
          Processing payment...
        </Typography>
      </div>
    )
  }

  if (stripeForm) {
    return <div className="mx-auto w-full max-w-2xl p-2">{stripeForm}</div>
  }

  const showProductDetails =
    order &&
    orderDetails &&
    productDetails &&
    productDetails.currency === order.currency.code &&
    productDetails.productPrice > orderDetails.itemTotal

  return (
    <div className="mx-auto w-full max-w-2xl">
      <div className="p-2">
        <img
          src="https://assets.suraasa.com/logos/v2/mini.webp"
          alt="suraasa logo"
          height={35}
          width={35}
          className="mb-4.5"
        />

        <div className="mb-4 flex flex-row items-center justify-between">
          <div>
            <Typography variant="preTitle" className="text-onSurface-500">
              1 year access
            </Typography>
            <Typography variant="title3" className="mt-1 text-onSurface-900">
              {order?.collectingOrganization.name}
            </Typography>
          </div>
          <div
            className="flex size-[84px] items-center justify-center overflow-hidden rounded-[7.52px] border-[2.15px] border-[#00000026] bg-cover bg-clip-padding bg-center bg-no-repeat"
            style={{
              backgroundImage: `url(${LogoBg})`,
              boxShadow: "0px 6.45px 16.12px 0px #0000000D",
            }}
          >
            <DefaultLogo name={order?.collectingOrganization.name} />
          </div>
        </div>
      </div>
      <div className="p-2">
        <Accordion type="single" collapsible>
          <AccordionItem value="features">
            <AccordionTrigger
              className="flex-row-reverse justify-end gap-1.25 px-0"
              icon={<NavArrowDown className="!size-2 text-onSurface-700" />}
            >
              <div className="flex flex-1 items-center justify-between">
                <Typography variant="strongSmallBody">
                  See what&apos;s included
                </Typography>
                <div className="flex items-center gap-[1.75px]">
                  <span className="flex size-3.5 items-center justify-center rounded-full bg-[#EDFAFE]">
                    <AISparkleFilled
                      fill="#22A9CE"
                      width="10px"
                      height="10px"
                    />
                  </span>
                  <span className="flex size-3.5 items-center justify-center rounded-full bg-[#FFF5FF]">
                    <AssessmentIcon className="size-2 text-[#AB25DE]" />
                  </span>
                  <span className="flex size-3.5 items-center justify-center rounded-full bg-[#EBF8FE]">
                    <Reports className="size-2 stroke-[1.8px] text-[#15638D]" />
                  </span>
                  <span className="flex size-3.5 items-center justify-center rounded-full bg-[#FCF5EE]">
                    <BookClosed className="size-2 text-[#B5610B]" />
                  </span>
                  <span className="flex size-3.5 items-center justify-center rounded-full bg-[#F4FDEB]">
                    <GraphUp className="size-2 stroke-[1.8px] text-[#62B206]" />
                  </span>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-0">
              <FeatureCard
                icon={
                  <AISparkleFilled
                    fill="#22A9CE"
                    width={"16px"}
                    height={"16px"}
                  />
                }
                title="AI-powered Activities"
                subtitle="Tailored to your current teaching level and ready to use in your next lesson."
                gradient="linear-gradient(180deg, #EDFAFE 0%, #F8F9FA 49.22%)"
              />
              <div className="mt-1.5 grid grid-cols-2 gap-x-2 gap-y-1.5">
                <FeatureCard
                  icon={<AssessmentIcon className="size-3 text-[#AB25DE]" />}
                  title="Assessment"
                  subtitle="Assesses teaching skills across 6 pedagogy domains"
                  gradient="linear-gradient(180deg, #FFF5FF 0%, #F8F9FA 49.22%)"
                />
                <FeatureCard
                  icon={
                    <Reports className="size-3 stroke-[1.8px] text-[#15638D]" />
                  }
                  title="Skill Graph"
                  subtitle="See your strengths and where to grow next."
                  gradient="linear-gradient(180deg, #EBF8FE 0%, #F8F9FA 49.22%)"
                />
                <FeatureCard
                  icon={<BookClosed className="size-3 text-[#B5610B]" />}
                  title="Learning Path"
                  subtitle="Modules that deepen your practice and help you build skills."
                  gradient="linear-gradient(180deg, #FCF5EE 0%, #F8F9FA 49.22%)"
                />
                <FeatureCard
                  icon={
                    <GraphUp className="size-3 stroke-[1.8px] text-[#62B206]" />
                  }
                  title="Growth Tracking"
                  subtitle="Every milestone and coaching feedback in one place."
                  gradient="linear-gradient(180deg, #F4FDEB 0%, #F8F9FA 49.22%)"
                />
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <div className="mt-1.5 border-b border-surface-100" />
      </div>
      <form id="payment-form" onSubmit={onSubmit} className="p-2">
        <TextField
          label="Full Name"
          errors={errors.fullName?.message}
          disabled
          {...register("fullName", {
            required: { value: true, message: "Required" },
          })}
        />
        <TextField
          className="mt-2.5"
          label="Email"
          type="email"
          disabled
          errors={errors.email?.message}
          {...register("email", {
            required: { value: true, message: "Required" },
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Enter a valid email",
            },
          })}
        />

        <div className="mb-1 mt-2.5">
          <InputLabel label="Phone Number (optional)" />
          <Controller
            control={control}
            name="phoneNumber"
            render={({ field }) => (
              <SplitTextField
                fullWidth
                error={Boolean(errors.phoneNumber)}
                helperText={
                  errors.phoneNumber?.number?.message ||
                  errors.phoneNumber?.code?.message ||
                  errors.phoneNumber?.message
                }
                selectProps={{
                  menuPlacement: "top",
                  placeholder: "+91",
                  options,
                  value: options.find(c => c.value === field?.value?.code),
                  onChange: val => {
                    if (val)
                      field.onChange({
                        ...field.value,
                        code: val.value,
                      })
                  },
                }}
                textFieldProps={{
                  type: "number",
                  placeholder: "Enter phone number",
                  value: field?.value?.number ?? undefined,
                  onChange: val => {
                    field.onChange({
                      ...field.value,
                      number: val.target.value,
                    })
                  },
                }}
              />
            )}
            rules={{
              validate: v => {
                if (!v?.number) return
                if (!v.code) return "Code Required"
              },
            }}
          />
        </div>

        <div className="mb-2.5">
          <div className="mt-2.5">
            <Checkbox
              checked={hasDifferentBilling}
              onCheckedChange={checked =>
                setHasDifferentBilling(checked === true)
              }
              containerClass="flex items-center gap-1"
              label={
                <Typography variant="body">
                  I have different billing details
                </Typography>
              }
            />
          </div>
          {hasDifferentBilling && (
            <>
              <Typography variant="largeBody" className="my-3">
                Billing Details
              </Typography>
              <TextField
                label="Name for Invoice"
                errors={errors.billingFullName?.message}
                {...register("billingFullName", {
                  required: { value: true, message: "Required" },
                })}
              />
              <TextField
                className="mt-2.5"
                label="Email for Invoice"
                type="email"
                errors={errors.billingEmail?.message}
                {...register("billingEmail", {
                  required: { value: true, message: "Required" },
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: "Enter a valid email",
                  },
                })}
              />
            </>
          )}
        </div>
      </form>
      <div className="flex flex-col bg-surface-50 p-2 shadow-[0px_-2px_6px_-2px_#0000001A]">
        <div className="flex flex-col">
          <Typography variant="title4" className="mb-4.5">
            Order Summary
          </Typography>

          {order && orderDetails && (
            <>
              {showProductDetails && (
                <>
                  <div className="mb-2 flex flex-row justify-between">
                    <Typography variant="body" className="text-onSurface-600">
                      Product Value
                    </Typography>
                    <Typography
                      variant="strong"
                      className="text-onSurface-600 line-through"
                    >
                      {getCurrencySymbol(order.currency.code)}
                      {formatPrice(productDetails.productPrice)}
                    </Typography>
                  </div>
                  <div className="mb-2 flex flex-row justify-between">
                    <Typography variant="body" className="text-success-500">
                      Discount
                    </Typography>
                    <Typography variant="strong" className="text-success-500">
                      -{getCurrencySymbol(order.currency.code)}
                      {formatPrice(
                        productDetails.productPrice - orderDetails.itemTotal
                      )}
                    </Typography>
                  </div>
                </>
              )}
              <div className="mb-2 flex flex-row justify-between">
                <Typography variant="body" className="text-onSurface-600">
                  Item
                </Typography>
                <Typography variant="strong" className="text-onSurface-600">
                  {getCurrencySymbol(order.currency.code)}
                  {formatPrice(orderDetails.itemTotal)}
                </Typography>
              </div>
              {showProductDetails && (
                <Divider weight="light" className="mb-2 bg-surface-200" />
              )}
              <div className="mb-2 flex flex-row justify-between">
                <Typography variant="body" className="text-onSurface-600">
                  Tax
                </Typography>
                <Typography variant="strong" className="text-onSurface-600">
                  {getCurrencySymbol(order.currency.code)}
                  {formatPrice(orderDetails.taxAmount)}
                </Typography>
              </div>
            </>
          )}

          <Divider weight="medium" className="mb-2 bg-surface-200" />

          {order && orderDetails && (
            <div className="mb-5 flex flex-row justify-between">
              <Typography variant="strong">Total</Typography>
              <Typography variant="title2">
                {getCurrencySymbol(order.currency.code)}
                {formatPrice(orderDetails.finalAmount)}
              </Typography>
            </div>
          )}
        </div>
        <Button
          className="mt-4 w-full"
          color="black"
          type="submit"
          form="payment-form"
          loading={isSubmitting}
        >
          Proceed to Pay
        </Button>
        {isIndia ? (
          <div className="mb-6 mt-1 flex flex-row items-center justify-center gap-0.5">
            <Typography variant={"smallBody"} className="text-onSurface-500">
              Secured by
            </Typography>
            <img src={RazorpayLogo} alt="Razorpay" className="h-2" />
          </div>
        ) : (
          <div className="mb-6 mt-1 flex flex-row items-center justify-center gap-0.5">
            <Typography variant={"smallBody"} className="text-onSurface-500">
              Powered by
            </Typography>
            <img src={StripeLogo} alt="stripe" className="h-2" />
          </div>
        )}

        <Typography
          variant={"smallBody"}
          className="text-center text-onSurface-500"
        >
          This fee is non-refundable. By proceeding you agree to accept our{" "}
          <a
            className="text-interactive-500"
            href={getPlatformURL("suraasa", "/terms-of-use")}
            target="_blank"
            rel="noreferrer"
          >
            {" "}
            terms & conditions
          </a>
        </Typography>
      </div>
    </div>
  )
}

export default EmbeddedPayment
